// ======================
// AzureStoragePlannerDescriptionStep
// ======================

import { AzureStoragePlannerStepProps, getAzureRegionDisplayName } from "./AzureStoragePlannerHelpers";
import { Box, Stack, Typography } from "@mui/material";
import { AzureStoragePlannerDescriptionFormFields, useAzureStoragePlannerState } from "./AzureStoragePlannerState";
import * as React from "react";
import * as yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { FormSelect, FormSelectableCardConfig, FormSelectableCardsGroup, FormTextField } from "../../../common/form/FormComponents";
import ManagedDisksOnly from "../../../assets/azure/managed_disks.png";
import ElasticSanAndManagedDisks from "../../../assets/azure/elastic_san_and_managed_disks.png";
import { AzureRegion, AzureStorageProductRedundancy } from "gc-web-proto/galaxycompletepb/apipb/domainpb/azure_pb";
import { StepperNavButtons } from "../../../common/stepper/StepperComponents";

import { useMountEffect } from "../../../common/hooks/hookslib";
import { useTranslation } from "react-i18next";

export const AzureStoragePlannerDescriptionStep: React.FC<AzureStoragePlannerStepProps> = (p) => {
    const { t } = useTranslation("integrations");
    const plannerState = useAzureStoragePlannerState();
    const initialValues = plannerState.descriptionFields;

    useMountEffect(() => {
        if (!plannerState.descriptionFields.planName) {
            if (plannerState.descriptionFieldsSubmitted) {
                plannerState.setDescriptionFieldsSubmitted(false);
            }
        }
    });

    const schema = yup.object({
        planName: yup.string().required(t("azurePlanner.description.validation.planName")),
        planDescription: yup.string(),
        analyzePeriodMinutes: yup.number().required(),
        storageGrowthPercent: yup.number().required(),
        workloadGrowthPercent: yup.number().required(),
        region: yup.number().required(t("azurePlanner.description.validation.region")),
        storageRedundancy: yup.number().required(),
        noElasticSan: yup.boolean().required(),
    });

    const ioAccessHistoryOptions: FormSelectableCardConfig[] = [
        {
            title: <Typography>{t("azurePlanner.description.historyOptions.fifteenMinutes")}</Typography>,
            value: 15,
            mainContentPadding: 0,
            cardProps: {
                sx: {
                    width: 150,
                },
            },
        },
        {
            title: <Typography>{t("azurePlanner.description.historyOptions.oneDay")}</Typography>,
            value: 1440,
            mainContentPadding: 0,
            cardProps: {
                sx: {
                    width: 150,
                },
            },
        },
        {
            title: <Typography>{t("azurePlanner.description.historyOptions.sevenDays")}</Typography>,
            value: 1440 * 7,
            mainContentPadding: 0,
            cardProps: {
                sx: {
                    width: 150,
                },
            },
        },
    ];

    const workloadGrowthValues = [-25, -10, 0, 10, 25];
    const workloadGrowthOptions: FormSelectableCardConfig[] = workloadGrowthValues.map((n) => {
        return {
            title: <Typography>{!!n ? `${n}%` : t("azurePlanner.description.noChange")}</Typography>,
            value: n,
            mainContentPadding: 0,
            cardProps: {
                sx: {
                    width: 150,
                },
            },
        };
    });

    const storageGrowthValues = [0, 10, 25, 50, 100];

    const storageGrowthOptions: FormSelectableCardConfig[] = storageGrowthValues.map((n) => {
        return {
            title: <Typography>{!!n ? `${n}%` : t("azurePlanner.description.noChange")}</Typography>,
            value: n,
            mainContentPadding: 0,
            cardProps: {
                sx: {
                    width: 150,
                },
            },
        };
    });

    const azureStorageProductOptions: FormSelectableCardConfig[] = [
        {
            icon: <img height={100} src={ElasticSanAndManagedDisks} alt={"Azure Elastic SAN + Azure Managed Disks"} />,
            title: "Azure Elastic SAN + Azure Managed Disks",
            value: false,
        },
        {
            icon: <img height={100} src={ManagedDisksOnly} alt={t("azurePlanner.description.managedDisksOnly")} />,
            title: t("azurePlanner.description.managedDisksOnly"),
            value: true,
        },
    ];

    const azureRegionSelectionList = Object.keys(AzureRegion).map((key) => {
        return {
            label: getAzureRegionDisplayName(key as keyof AzureRegion),
            value: AzureRegion[key as keyof typeof AzureRegion],
        };
    });

    const submit = async (values: AzureStoragePlannerDescriptionFormFields, formikHelpers: FormikHelpers<AzureStoragePlannerDescriptionFormFields>) => {
        plannerState.setDescriptionFields(values);
        plannerState.setDescriptionFieldsSubmitted(true);
    };

    return (
        <Formik initialValues={initialValues} validationSchema={schema} onSubmit={submit}>
            {(props) => {
                const getContinueDisabled = () => {
                    return plannerState.descriptionFieldsSubmitted ? false : !props.isValid || !props.dirty;
                };
                return (
                    <>
                        <Typography variant={"h5"}>{t("azurePlanner.description.sections.description")}</Typography>
                        <Typography>{t("azurePlanner.description.summaryHelp")}</Typography>
                        <Box pt={2} pb={2}>
                            <Box pb={2}>
                                <FormTextField
                                    variant={"filled"}
                                    required
                                    placeholder={t("azurePlanner.description.planNamePlaceholder")}
                                    label={t("azurePlanner.description.planName")}
                                    name={"planName"}
                                    fullWidth
                                />
                            </Box>
                            <Box>
                                <FormTextField variant={"filled"} label={t("azurePlanner.description.planDescription")} fullWidth name={"planDescription"} />
                            </Box>
                        </Box>
                        <Typography variant={"h5"}>{t("azurePlanner.description.sections.preferences")}</Typography>
                        <Typography>{t("azurePlanner.description.preferencesHelp")}</Typography>
                        <Stack pt={2} pb={2} spacing={2}>
                            <Typography variant={"h6"}>{t("azurePlanner.description.historyQuestion")}</Typography>
                            <FormSelectableCardsGroup options={ioAccessHistoryOptions} name={"analyzePeriodMinutes"} />
                        </Stack>
                        <Stack pt={2} pb={2} spacing={2}>
                            <Typography variant={"h6"}>{t("azurePlanner.description.workloadGrowthQuestion")}</Typography>
                            <FormSelectableCardsGroup options={workloadGrowthOptions} name={"workloadGrowthPercent"} />
                        </Stack>
                        <Stack pt={2} pb={2} spacing={2}>
                            <Typography variant={"h6"}>{t("azurePlanner.description.storageGrowthQuestion")}</Typography>
                            <FormSelectableCardsGroup options={storageGrowthOptions} name={"storageGrowthPercent"} />
                        </Stack>
                        <Stack pt={2} pb={2} spacing={2}>
                            <Typography variant={"h6"}>{t("azurePlanner.description.productQuestion")}</Typography>
                            <FormSelectableCardsGroup options={azureStorageProductOptions} name={"noElasticSan"} />
                        </Stack>
                        <Box pt={2} pb={2}>
                            <Box pb={2}>
                                <FormSelect name={"region"} label={t("azurePlanner.description.region")} selectionList={azureRegionSelectionList} />
                            </Box>
                            <Box>
                                <FormSelect
                                    name={"storageRedundancy"}
                                    label={t("azurePlanner.description.redundancy")}
                                    selectionList={[
                                        {
                                            label: t("azurePlanner.description.redundancyOptions.local"),
                                            value: AzureStorageProductRedundancy.AZURE_STORAGE_REDUNDANCY_LOCAL,
                                        },
                                        {
                                            label: t("azurePlanner.description.redundancyOptions.zonal"),
                                            value: AzureStorageProductRedundancy.AZURE_STORAGE_REDUNDANCY_ZONAL,
                                        },
                                    ]}
                                />
                            </Box>
                        </Box>
                        <StepperNavButtons
                            nextButtonProps={{
                                disabled: getContinueDisabled(),
                                onClick: props.submitForm,
                            }}
                        />
                    </>
                );
            }}
        </Formik>
    );
};
